import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { DomainURL, DomainURLUpdatePayload } from '@core/models/domain-url.model';
import { DomainURLDataService, DomainURLDuplicateConflict } from '../services/domain-url-data.service';
import { AppPermissionService } from '@core/services/app-permission.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kt-domain-url-edit',
  templateUrl: './domain-url-edit.component.html',
  styleUrls: ['./domain-url-edit.component.scss']
})
export class DomainURLEditComponent implements OnInit, OnDestroy {

  form: FormGroup;
  buttonLoading = false;
  messages$ = this.domainURLDataService.messages$;
  dropdown = {
    regions: []
  };

  private subscriptions = new Subscription();

  // permissions
  canEditDomainURL: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { mode: string, domainURL: DomainURL },
    public dialogRef: MatDialogRef<DomainURLEditComponent>,
    private domainURLDataService: DomainURLDataService,
    private appPermissionService: AppPermissionService,
    private dropdownHttpService: DropdownHttpService,
  ) { }

  ngOnInit() {
    this.formInit();

    const regionSub = this.dropdownHttpService.regions.subscribe(res => {
      this.dropdown.regions = res;
      this.patchSettingsCurrencyIdIfMissing();
    });

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canEditDomainURL = appPermissions.edit_domain_url;
    });

    this.subscriptions.add(regionSub);
    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onCloseDialog(value = false) {
    this.dialogRef.close(value);
  }

  onSave() {
    if (this.data.mode !== 'edit' || !this.data.domainURL?.id) {
      this.resetSaveState();
      return;
    }

    this.buttonLoading = true;
    // To set "Save" button to disable (To prevent call API in multiple times when double click)
    this.form.setErrors({ invalid: true });
    const payload = this.buildUpdatePayload();
    const overlappingLandingDomains = this.findOverlappingLandingDomains(payload);
    const duplicateNonLandingDomains = this.findDuplicateNonLandingDomains(payload);

    if (overlappingLandingDomains.length > 0) {
      this.showDuplicateWarning(
        'Duplicate Landing Domain Detected',
        `<b>${overlappingLandingDomains.join(', ')}</b> cannot be used in both <b>Primary Landing</b> and <b>Hidden Landing</b> for the same region. Please update the domains and try again.`,
      );
      return;
    }

    if (duplicateNonLandingDomains.length > 0) {
      this.showDuplicateWarning(
        'Duplicate Domain Detected',
        `<b>${duplicateNonLandingDomains.join(', ')}</b> cannot be used more than once in <b>Main</b> or <b>Active</b> for the same region. Please update the domains and try again.`,
      );
      return;
    }

    this.checkDuplicatesBeforeSave(payload);
  }

  private checkDuplicatesBeforeSave(payload: DomainURLUpdatePayload) {
    this.domainURLDataService.checkDuplicates({
      id: this.data.domainURL.id,
      settings_currency_id: payload.settings_currency_id,
      priority_data: payload.priority_data,
    }).subscribe(
      (conflicts: DomainURLDuplicateConflict[]) => {
        if (conflicts.length === 0) {
          this.performSave(payload);
          return;
        }

        const conflictList = conflicts
          .map(conflict => {
            const region = conflict.region ? `${conflict.region} region ` : '';
            return `<b>${conflict.duplicate_domains.join(', ')}</b> is already in <b>${region}${conflict.priority_type_name} ${conflict.domain_type_name}</b> record.`;
          })
          .join('<br>');

        Swal.fire({
          title: 'Duplicate Domain Detected',
          html: `${conflictList}<br><br>Pressing OK will assign the domain to the current record. The duplicate will be removed from the other record(s).`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
        }).then(result => {
          if (result.value) {
            this.performSave(payload);
          } else {
            this.resetSaveState();
          }
        });
      },
      error => {
        const duplicateDomains = this.extractDuplicateDomainsFromError(error);

        if (duplicateDomains.length > 0) {
          this.showDuplicateWarning(
            'Duplicate Domain Detected',
            `<b>${duplicateDomains.join(', ')}</b> cannot be used more than once in <b>Main</b> or <b>Active</b> for the same region. Please update the domains and try again.`,
          );
          return;
        }

        this.showSystemError(error);
      }
    );
  }

  private resetSaveState() {
    this.buttonLoading = false;
    this.form.setErrors(null);
  }

  private showDuplicateWarning(title: string, html: string) {
    Swal.fire({
      title,
      html,
      icon: 'error',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK',
    }).then(() => {
      this.resetSaveState();
    });
  }

  private showSystemError(error: any) {
    const system = error?.error ?? error;
    const rawMessages = system?.message ?? system?.messages ?? [];
    const messages = (Array.isArray(rawMessages) ? rawMessages : [rawMessages]).filter(Boolean);

    if (messages.length > 0) {
      Swal.fire({
        title: 'System Message',
        html: `<ul>${messages.map(message => `<li>${message}</li>`).join('')}</ul>`,
        icon: 'error',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK',
      }).then(() => {
        this.resetSaveState();
      });
      return;
    }

    this.resetSaveState();
  }

  private performSave(data: DomainURLUpdatePayload) {
    this.domainURLDataService.update(data).pipe(
      tap(_ => { this.buttonLoading = false; }),
      catchError(error => {
        this.buttonLoading = false;
        this.form.setErrors(null);
        throw error;
      })
    ).subscribe();
  }

  private buildUpdatePayload(): DomainURLUpdatePayload {
    const raw = this.form.getRawValue();
    return {
      settings_currency_id: raw.settings_currency_id,
      priority_data: [
        {
          priority_type: 1,
          domain_data: [
            { domain_type: 1, domains: this.formatDomains(raw.primary_landing) },
            { domain_type: 2, domains: this.formatDomains(raw.primary_main) },
            { domain_type: 3, domains: this.formatDomains(raw.primary_active) }
          ]
        },
        {
          priority_type: 2,
          domain_data: [
            { domain_type: 1, domains: this.formatDomains(raw.hidden_landing) },
            { domain_type: 2, domains: this.formatDomains(raw.hidden_main) },
            { domain_type: 3, domains: this.formatDomains(raw.hidden_active) }
          ]
        }
      ]
    };
  }

  private formatDomains(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .split(/[\n,]+/)
      .map(item => item.trim())
      .filter(Boolean)
      .join(',');
  }

  private toDomainArray(value?: string | null): string[] {
    if (!value) {
      return [];
    }

    return value
      .split(/[\n,]+/)
      .map(item => item.trim())
      .filter(Boolean);
  }

  private findOverlappingLandingDomains(payload: DomainURLUpdatePayload): string[] {
    const primaryLandingDomains = this.toDomainArray(payload.priority_data?.find(item => item.priority_type === 1)?.domain_data?.find(item => item.domain_type === 1)?.domains);
    const hiddenLandingDomains = this.toDomainArray(payload.priority_data?.find(item => item.priority_type === 2)?.domain_data?.find(item => item.domain_type === 1)?.domains);
    const hiddenLandingLookup = new Set(hiddenLandingDomains.map(domain => domain.toLowerCase()));

    return primaryLandingDomains.filter((domain, index, domains) =>
      hiddenLandingLookup.has(domain.toLowerCase())
      && domains.findIndex(item => item.toLowerCase() === domain.toLowerCase()) === index
    );
  }

  private findDuplicateNonLandingDomains(payload: DomainURLUpdatePayload): string[] {
    const nonLandingDomains = (payload.priority_data || []).reduce((domains, priorityItem) => {
      (priorityItem.domain_data || []).forEach(domainItem => {
        if (domainItem.domain_type !== 1) {
          domains.push(...this.toDomainArray(domainItem.domains));
        }
      });

      return domains;
    }, [] as string[]);

    return this.findDuplicateDomains(nonLandingDomains);
  }

  private findDuplicateDomains(domains: string[]): string[] {
    const seen = new Map<string, string>();
    const duplicates = new Set<string>();

    domains.forEach(domain => {
      const normalizedDomain = domain.toLowerCase();

      if (seen.has(normalizedDomain)) {
        duplicates.add(seen.get(normalizedDomain) || domain);
        return;
      }

      seen.set(normalizedDomain, domain);
    });

    return Array.from(duplicates);
  }

  private extractDuplicateDomainsFromError(error: any): string[] {
    const system = error?.error ?? error;
    const rawMessages = system?.message ?? system?.messages ?? [];
    const messages = Array.isArray(rawMessages) ? rawMessages : [rawMessages];

    const duplicateMessage = messages.find(message =>
      typeof message === 'string' && message.includes('Duplicate domains are not allowed:')
    );

    if (!duplicateMessage) {
      return [];
    }

    return duplicateMessage
      .replace('Duplicate domains are not allowed:', '')
      .split(',')
      .map((item: string) => item.trim())
      .filter(Boolean);
  }

  private toDomainText(priorityName: 'Primary' | 'Hidden', domainType: 'Landing' | 'Main' | 'Active'): string {
    const domains = this.data?.domainURL?.priority_type?.[priorityName]?.domain_type?.[domainType] || [];
    return Array.isArray(domains) ? domains.join(',') : '';
  }

  private resolveSettingsCurrencyId(): number {
    if (this.data?.domainURL?.settings_currency_id) {
      return this.data.domainURL.settings_currency_id;
    }

    const regionCode = this.data?.domainURL?.region;
    const matchedRegion = this.dropdown.regions.find(item => item.country_code === regionCode);
    return matchedRegion?.settings_currency_id || null;
  }

  private patchSettingsCurrencyIdIfMissing() {
    const control = this.form?.get('settings_currency_id');
    if (!control || control.value) {
      return;
    }

    const settingsCurrencyId = this.resolveSettingsCurrencyId();
    if (settingsCurrencyId) {
      control.patchValue(settingsCurrencyId);
    }
  }

  private formInit() {
    this.form = new FormGroup({
      settings_currency_id: new FormControl(this.resolveSettingsCurrencyId(), [Validators.required]),
      primary_landing: new FormControl(this.toDomainText('Primary', 'Landing'), [Validators.required]),
      primary_main: new FormControl(this.toDomainText('Primary', 'Main'), [Validators.required]),
      primary_active: new FormControl(this.toDomainText('Primary', 'Active')),
      hidden_landing: new FormControl(this.toDomainText('Hidden', 'Landing'), [Validators.required]),
      hidden_main: new FormControl(this.toDomainText('Hidden', 'Main'), [Validators.required]),
      hidden_active: new FormControl(this.toDomainText('Hidden', 'Active')),
    });
  }
}
